// src/components/layout/Sidebar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  School,
  Users,
  BookOpen,
  GraduationCap,
  UserCheck,
  FileText,
  Settings,
  BarChart2,
  MessageSquare,
  MessageCircle,
  Gamepad,
  Zap,
  Edit3,
  Cpu,
  FileCode2,
  Image,
  Pen,
  ClipboardList,
  CreditCard,
  Gauge,
  Tag,
  ChevronDown,
  ChevronRight,
  Activity,
  LineChart,
  Wallet,
  Bell,
  Home,
  Music2,
  LifeBuoy,
  Sparkles,
} from "lucide-react";
import { clsx } from "clsx";
import { ROUTES, USER_ROLES } from "../../utils/constants";
import { usePrefetchReadingComprehension } from "../../modules/reading/usePrefetchReadingComprehension";
import { useTranslation } from "react-i18next";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import type { SchoolStudentFeatureKey } from "../../modules/platform/types/featureAccess";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { useTutorTenantLabels } from "../../modules/tutor/hooks/useTutorTenantLabels";
import { useLearnerCurriculum } from "../../modules/curriculum/hooks/useLearnerCurriculum";
import { DEV_AUTHORING_SIDEBAR_ITEM } from './sidebarDevAuthoringNav';
import { DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM } from './sidebarDevInteractiveBooksNav';
import {
  buildStudentPocNavItems,
  resolveLearnerTrackContext,
  type StudentPocNavIconKey,
} from "../../modules/student-nav/studentPocNavigation";
import { SidebarMyProgress } from "./SidebarMyProgress";
import { isNavItemActive, type SidebarNavItemLike } from "./sidebarNavActive";
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemChild {
  labelKey: string;
  route: string;
  icon: React.ElementType;
}

interface NavItem extends SidebarNavItemLike {
  labelKey: string;
  icon: React.ElementType;
  roles: string[];
  /** When set, item is an accordion parent with submenu */
  children?: NavItemChild[];
  /** Gated by GET /platform/feature-access/me for students; omit for always-visible items */
  schoolFeatureKey?: SchoolStudentFeatureKey;
  /** Show when any listed school feature is enabled (overrides single key when set). */
  schoolFeatureKeysAny?: SchoolStudentFeatureKey[];
}

type SupportNavMode = 'operator' | 'customer-school' | 'customer';

function buildSupportNavItem(
  baseRoute: string,
  roles: string[],
  mode: SupportNavMode
): NavItem {
  const children: NavItemChild[] = [];

  if (mode === 'operator') {
    children.push(
      { labelKey: "sidebar.support.home", route: baseRoute, icon: LifeBuoy },
      { labelKey: "sidebar.support.dashboard", route: `${baseRoute}/dashboard`, icon: BarChart2 },
      { labelKey: "sidebar.support.queue", route: `${baseRoute}/queue`, icon: ClipboardList },
      { labelKey: "sidebar.support.allTickets", route: `${baseRoute}/tickets`, icon: FileText },
      { labelKey: "sidebar.support.createTicket", route: `${baseRoute}/tickets/new`, icon: Edit3 },
      { labelKey: "sidebar.support.helpCenter", route: `${baseRoute}/help`, icon: BookOpen },
      { labelKey: "sidebar.support.knowledgeAdmin", route: `${baseRoute}/knowledge`, icon: BookOpen },
    );
  } else if (mode === 'customer-school') {
    children.push(
      { labelKey: "sidebar.support.home", route: baseRoute, icon: LifeBuoy },
      { labelKey: "sidebar.support.myTickets", route: `${baseRoute}/tickets`, icon: FileText },
      { labelKey: "sidebar.support.createTicket", route: `${baseRoute}/tickets/new`, icon: Edit3 },
      { labelKey: "sidebar.support.helpCenter", route: `${baseRoute}/help`, icon: BookOpen },
    );
  } else {
    children.push(
      { labelKey: "sidebar.support.myTickets", route: `${baseRoute}/tickets`, icon: FileText },
      { labelKey: "sidebar.support.createTicket", route: `${baseRoute}/tickets/new`, icon: Edit3 },
      { labelKey: "sidebar.support.helpCenter", route: `${baseRoute}/help`, icon: BookOpen },
    );
  }

  const activeRoutes = [
    baseRoute,
    `${baseRoute}/help`,
    `${baseRoute}/dashboard`,
    `${baseRoute}/tickets`,
    `${baseRoute}/tickets/new`,
    `${baseRoute}/queue`,
    `${baseRoute}/knowledge`,
    `${baseRoute}/knowledge/categories`,
    `${baseRoute}/knowledge/articles`,
  ];
  return {
    labelKey: "sidebar.support",
    icon: LifeBuoy,
    route: baseRoute,
    roles,
    activeRoutes,
    children,
  };
}

// ✅ English-only sidebar labels (no t())
const LABELS: Record<string, string> = {
  // DEV TEAM
  "sidebar.dev.dashboard": "Dashboard",
  "sidebar.dev.schools": "Schools",
  "sidebar.dev.auditLogs": "Audit Logs",
  "sidebar.dev.auditConsole": "Audit console (Mongo)",
  "sidebar.dev.platformAnalytics": "Platform analytics",
  "sidebar.dev.studentUsage": "Student usage",
  "sidebar.dev.plans": "Plan management",
  "sidebar.dev.subscriptions": "School subscriptions",
  "sidebar.dev.webhooks": "Stripe webhooks",
  "sidebar.dev.billingAnalytics": "Billing analytics",
  "sidebar.dev.coupons": "Coupons",
  "sidebar.dev.emailConfigs": "Email Configs",
  "sidebar.dev.emailTemplates": "Email Templates",
  "sidebar.dev.promptTemplates": "Prompt library",
  "sidebar.dev.ibook": "iBook",
  "sidebar.dev.studentTranscripts": "Student Transcripts",
  "sidebar.dev.tutorQuotas": "Tutor quotas",
  "sidebar.dev.quotaDashboard": "Quota dashboard",
  "sidebar.dev.subscriptionAlerts": "Subscription Alerts",
  "sidebar.dev.alerts": "Operational alerts",
  "sidebar.dev.business": "Business dashboard",
  "sidebar.dev.compliance": "Compliance",
  "sidebar.dev.inviteDev": "Invite Dev",
  "sidebar.dev.weeklyReports": "Weekly Reports",
  "sidebar.dev.interactiveBookAuthoring": "Interactive Book Authoring",
  "sidebar.dev.interactiveBooks": "Interactive Books",
  "sidebar.support": "Support",
  "sidebar.support.home": "Support Center",
  "sidebar.support.dashboard": "Dashboard",
  "sidebar.support.myTickets": "My Tickets",
  "sidebar.support.allTickets": "All Tickets",
  "sidebar.support.createTicket": "Create Ticket",
  "sidebar.support.queue": "Agent Queue",
  "sidebar.support.knowledgeBase": "Knowledge Base",
  "sidebar.support.helpCenter": "Help Center",
  "sidebar.support.knowledgeAdmin": "Knowledge Admin",

  // SCHOOL ADMIN
  "sidebar.admin.dashboard": "Dashboard",
  "sidebar.admin.schoolProfile": "School Profile",
  "sidebar.admin.grades": "Classes",
  "sidebar.admin.sections": "Sections",
  "sidebar.admin.people": "People",
  "sidebar.admin.teachers": "Teachers",
  "sidebar.admin.students": "Students",
  "sidebar.admin.parents": "Parents",
  "sidebar.admin.billing": "Billing",
  "sidebar.admin.scenarioAuthoring": "Scenario Authoring",
  "sidebar.admin.revisionVocabulary": "Revision Vocabulary",
  "sidebar.admin.billingHistory": "Billing history",
  "sidebar.admin.myPlan": "My Plan",
  "sidebar.admin.tutorQuotas": "Tutor quotas",
  "sidebar.admin.quotaDashboard": "Quota dashboard",
  "sidebar.admin.analytics": "Analytics",
  "sidebar.admin.activityLogs": "Activity logs",
  "sidebar.admin.studentUsage": "Student usage",
  "sidebar.admin.emailConfigs": "Email Configs",
  "sidebar.admin.emailTemplates": "Email Templates",
  "sidebar.admin.weeklyReports": "Weekly Reports",
  "sidebar.admin.tutorTheme": "Tutor Theme Management",

  // TEACHER
  "sidebar.teacher.dashboard": "Dashboard",
  "sidebar.teacher.mySections": "My Sections",
  "sidebar.teacher.assignments": "Assignments",
  "sidebar.teacher.analytics": "Analytics",
  "sidebar.teacher.tutorAnalytics": "Analytics",
  "sidebar.teacher.canDo": "Can Do",
  // "sidebar.teacher.commAssignments": "Communication assignments",
  // "sidebar.teacher.commAnalytics": "Communication analytics",
  // "sidebar.teacher.commAlerts": "Communication alerts",
  "sidebar.teacher.scenarioCanDo": "Scenario Can-Do",
  "sidebar.teacher.scenarioAuthoring": "Scenario Authoring",
  "sidebar.teacher.content": "Content Management",
  "sidebar.teacher.profile": "Profile",
  "sidebar.teacher.settings": "Settings",

  // STUDENT
  "sidebar.student.home": "Home",
  "sidebar.student.chat": "Conversation",
  "sidebar.student.tamilTutor": "Tamil Tutor",
  "sidebar.student.games": "Games",
  "sidebar.student.phonicsLab": "Phonics Lab",
  "sidebar.student.vocabulary": "Vocabulary",
  "sidebar.student.ibooks": "IBooks",
  // "sidebar.student.courses": "Courses",
  "sidebar.student.assignments": "Assignments",
  "sidebar.student.activity": "Activity",
  "sidebar.student.storyStudio": "Story Studio",
  "sidebar.student.aiTutor": "AI Tutor",
  "sidebar.student.communicationTutor": "Communication Tutor",
  "sidebar.student.readingComprehension": "Reading Comprehension",
  "sidebar.student.keeladiReading": "Keeladi Reading & Speaking",
  "sidebar.student.tamilComposition": "Composition Writing",
  "sidebar.student.expositoryWriting": "Expository Writing",
  "sidebar.student.commAssignments": "Communication practice tasks",
  "sidebar.student.scenarioConversation": "Scenario Conversation",
  "sidebar.student.pictureTalk": "Picture Talk",
  "sidebar.student.writing": "Writing",
  "sidebar.student.library": "Library",
  "sidebar.student.progress": "Progress",
  "sidebar.student.analytics": "Analytics",
  "sidebar.student.settings": "Settings",
  "sidebar.student.profile": "Profile",

  // PARENT
  "sidebar.parent.dashboard": "Dashboard",
  "sidebar.parent.children": "Children",
  "sidebar.parent.linkChild": "Link Child",
  "sidebar.parent.progress": "Learning progress",
};

const PEOPLE_ROUTES = [ROUTES.ADMIN_STUDENTS, ROUTES.ADMIN_TEACHERS, ROUTES.ADMIN_PARENTS] as const;
const isPeopleRoute = (pathname: string) =>
  PEOPLE_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"));

/** Lavender card nav — active item with left pill + soft glow. */
const NAV_CARD =
  "relative flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm transition-all duration-200 bg-white dark:bg-gray-900 shadow-sm border";
const NAV_ACTIVE =
  "border-app-primary/40 dark:border-app-primary/50 ring-2 ring-app-primary/30 dark:ring-app-primary/35";
const NAV_INACTIVE =
  "border-app-primary/15 dark:border-app-primary/25 hover:border-app-primary/30 dark:hover:border-app-primary/40 hover:shadow-md";
const NAV_LABEL_ACTIVE = "text-slate-950 dark:text-white font-semibold";
const NAV_LABEL_INACTIVE = "text-slate-700 dark:text-slate-200 font-medium";
const NAV_ICON_WRAP =
  "w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-app-primary/10 dark:bg-app-primary/15 text-app-primary dark:text-app-primary/90";
const NAV_ICON_ACTIVE = "bg-app-primary/15 dark:bg-app-primary/25 text-app-primary dark:text-app-primary";

const SIDEBAR_NAV_SKELETON_COUNT = 7;

const POC_NAV_ICONS: Record<StudentPocNavIconKey, React.ElementType> = {
  home: Home,
  bookOpen: BookOpen,
  messageCircle: MessageCircle,
  pen: Pen,
};

function SidebarNavListSkeleton() {
  return (
    <>
      {Array.from({ length: SIDEBAR_NAV_SKELETON_COUNT }, (_, index) => (
        <div
          key={`sidebar-nav-skeleton-${index}`}
          className={clsx(NAV_CARD, NAV_INACTIVE, "animate-pulse pointer-events-none")}
          aria-hidden
        >
          <div className={clsx(NAV_ICON_WRAP, "bg-app-primary/5 dark:bg-app-primary/10")} />
          <div
            className="h-4 flex-1 rounded-md bg-slate-200/90 dark:bg-gray-700/70"
            style={{ maxWidth: `${58 + (index % 3) * 12}%` }}
          />
        </div>
      ))}
    </>
  );
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { loading: featureAccessLoading, isSchoolFeatureEnabled } = useFeatureAccess();
  const { grade: rosterGrade, isRosterLoading } = useStudentMe();
  const { labels: tutorTenantLabels, loading: tutorLabelsLoading } = useTutorTenantLabels();
  const { curriculum, loading: curriculumLoading, error: curriculumError, reload: reloadCurriculum } = useLearnerCurriculum();
  const legacyTenantIsMalay = tutorTenantLabels.isMalay;
  const learnerTrack = resolveLearnerTrackContext({
    curriculum,
    legacyTenantIsMalay,
    curriculumLoading,
  });
  const { track: learnerPocTrack, isLearnerMalay, isLearnerTamil } = learnerTrack;
  const location = useLocation();
  const { t } = useTranslation();
  const [peopleOpen, setPeopleOpen] = useState(() => isPeopleRoute(location.pathname));

  const gradeNum =
    typeof rosterGrade === "number" ? rosterGrade : parseInt(String(rosterGrade ?? ""), 10);
  usePrefetchReadingComprehension(
    Boolean(user?.roles.includes(USER_ROLES.STUDENT)) &&
      !isLearnerTamil &&
      legacyTenantIsMalay &&
      !featureAccessLoading &&
      isSchoolFeatureEnabled("reading_comprehension") &&
      !isRosterLoading &&
      Number.isFinite(gradeNum) &&
      gradeNum === 7,
  );

  useEffect(() => {
    if (isPeopleRoute(location.pathname)) setPeopleOpen(true);
  }, [location.pathname]);

  if (!user) return null;

  /** Labels gate skeleton; curriculum + feature access defer track/items without blocking nav shell. */
  const sidebarReady = !tutorLabelsLoading;

  const navLabel = (key: string) => {
    if (key === 'sidebar.student.tamilTutor') return tutorTenantLabels.moduleTitleAiTutor;
    if (key === 'sidebar.student.communicationTutor') {
      return 'Narrative Writing';
    }
    if (key === 'sidebar.student.expositoryWriting') {
      return t('sidebar.student.expositoryWriting', {
        defaultValue: 'Expository Writing',
      });
    }
    if (key === 'sidebar.student.readingComprehension') {
      return t('sidebar.student.readingComprehension', {
        defaultValue: 'Pemahaman Bacaan',
      });
    }
    if (key === 'sidebar.student.keeladiReading') {
      return t('sidebar.student.keeladiReading', {
        defaultValue: 'கீழடி காட்டும் தமிழர் வாழ்வியல் — Reading & Speaking',
      });
    }
    if (key === 'sidebar.student.tamilComposition') {
      return t('sidebar.student.tamilComposition', {
        defaultValue: 'விளக்கக் கட்டுரை — Composition Writing',
      });
    }
    return t(key, { defaultValue: LABELS[key] ?? key });
  };

  const isStudent = user.roles.includes(USER_ROLES.STUDENT);
  const isPocIbooksOnlyStudent =
    isStudent && (learnerPocTrack === 'tamil' || learnerPocTrack === 'malay');

  const navItems: NavItem[] = [];

  // DEV TEAM
  if (user.roles.includes(USER_ROLES.DEV_TEAM)) {
    navItems.push(
      { labelKey: "sidebar.dev.dashboard", icon: LayoutDashboard, route: ROUTES.DEV_DASHBOARD, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.schools", icon: School, route: ROUTES.DEV_SCHOOLS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.auditLogs", icon: FileText, route: ROUTES.DEV_AUDIT_LOGS, roles: [USER_ROLES.DEV_TEAM] },
      {
        labelKey: "sidebar.dev.auditConsole",
        icon: Activity,
        route: `${ROUTES.DEV_AUDIT_CONSOLE}/overview`,
        roles: [USER_ROLES.DEV_TEAM],
        activeRoutes: [ROUTES.DEV_AUDIT_CONSOLE, `${ROUTES.DEV_AUDIT_CONSOLE}/`],
      },
      { labelKey: "sidebar.dev.platformAnalytics", icon: LineChart, route: ROUTES.DEV_ANALYTICS, roles: [USER_ROLES.DEV_TEAM], exactActiveRoutes: [ROUTES.DEV_ANALYTICS] },
      {
        labelKey: "sidebar.dev.studentUsage",
        icon: BarChart2,
        route: ROUTES.DEV_ANALYTICS_STUDENTS,
        roles: [USER_ROLES.DEV_TEAM],
        activeRoutes: [ROUTES.DEV_ANALYTICS_STUDENTS],
      },
      { labelKey: "sidebar.dev.coupons", icon: Tag, route: ROUTES.PROFILE_COUPONS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.emailConfigs", icon: Settings, route: ROUTES.DEV_COMMS_EMAIL_CONFIGS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.emailTemplates", icon: FileText, route: ROUTES.DEV_COMMS_EMAIL_TEMPLATES, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.promptTemplates", icon: FileCode2, route: ROUTES.DEV_PROMPT_TEMPLATES, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.ibook", icon: BookOpen, route: ROUTES.DEV_IBOOK_LIST, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.tutorQuotas", icon: Gauge, route: ROUTES.DEV_TUTOR_QUOTAS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.quotaDashboard", icon: Gauge, route: ROUTES.ADMIN_QUOTA_DASHBOARD, roles: [USER_ROLES.DEV_TEAM] },
      {
        labelKey: "sidebar.dev.studentTranscripts",
        icon: FileText,
        route: ROUTES.DEV_STUDENT_TRANSCRIPTS,
        roles: [USER_ROLES.DEV_TEAM],
        activeRoutes: [ROUTES.DEV_STUDENT_TRANSCRIPTS],
      },
      { labelKey: "sidebar.dev.plans", icon: CreditCard, route: ROUTES.DEV_PLANS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.subscriptions", icon: CreditCard, route: ROUTES.DEV_SUBSCRIPTIONS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.webhooks", icon: Activity, route: ROUTES.DEV_WEBHOOKS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.billingAnalytics", icon: LineChart, route: ROUTES.DEV_BILLING_ANALYTICS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.subscriptionAlerts", icon: Bell, route: ROUTES.DEV_SUBSCRIPTION_ALERTS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.alerts", icon: Bell, route: ROUTES.DEV_ALERTS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.business", icon: LineChart, route: ROUTES.DEV_BUSINESS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.compliance", icon: FileText, route: ROUTES.DEV_COMPLIANCE, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.inviteDev", icon: UserCheck, route: ROUTES.DEV_INVITE, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.weeklyReports", icon: BarChart2, route: ROUTES.DEV_WEEKLY_REPORTS, roles: [USER_ROLES.DEV_TEAM] },
      {
        labelKey: DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM.labelKey,
        icon: DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM.icon,
        route: DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM.route,
        roles: [...DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM.roles],
        activeRoutes: [...DEV_INTERACTIVE_BOOKS_SIDEBAR_ITEM.activeRoutes],
      },
      {
        labelKey: DEV_AUTHORING_SIDEBAR_ITEM.labelKey,
        icon: DEV_AUTHORING_SIDEBAR_ITEM.icon,
        route: DEV_AUTHORING_SIDEBAR_ITEM.route,
        roles: [...DEV_AUTHORING_SIDEBAR_ITEM.roles],
        activeRoutes: [...DEV_AUTHORING_SIDEBAR_ITEM.activeRoutes],
      },
      { labelKey: "sidebar.admin.tutorTheme", icon: Sparkles, route: ROUTES.ADMIN_TUTOR_THEME, roles: [USER_ROLES.DEV_TEAM] },
      buildSupportNavItem(ROUTES.DEV_SUPPORT, [USER_ROLES.DEV_TEAM], 'operator')
    );
  }

  // SCHOOL ADMIN (People = Students + Teachers + Parents under one item; sub-sidebar shows the three)
  if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) {
    navItems.push(
      { labelKey: "sidebar.admin.dashboard", icon: LayoutDashboard, route: ROUTES.ADMIN_DASHBOARD, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.schoolProfile", icon: School, route: ROUTES.ADMIN_SCHOOL, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.grades", icon: BookOpen, route: ROUTES.ADMIN_CLASSES, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.sections", icon: Users, route: ROUTES.ADMIN_SECTIONS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      {
        labelKey: "sidebar.admin.people",
        icon: Users,
        route: ROUTES.ADMIN_STUDENTS,
        roles: [USER_ROLES.SCHOOL_ADMIN],
        activeRoutes: [ROUTES.ADMIN_STUDENTS, ROUTES.ADMIN_TEACHERS, ROUTES.ADMIN_PARENTS],
        children: [
          { labelKey: "sidebar.admin.teachers", route: ROUTES.ADMIN_TEACHERS, icon: Users },
          { labelKey: "sidebar.admin.students", route: ROUTES.ADMIN_STUDENTS, icon: GraduationCap },
          { labelKey: "sidebar.admin.parents", route: ROUTES.ADMIN_PARENTS, icon: UserCheck },
        ],
      },
      { labelKey: "sidebar.admin.billing", icon: CreditCard, route: ROUTES.ADMIN_BILLING, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.scenarioAuthoring", icon: MessageSquare, route: ROUTES.ADMIN_SCENARIO_AUTHORING, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.revisionVocabulary", icon: BookOpen, route: ROUTES.ADMIN_CONTENT_REVISION_VOCABULARY, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.myPlan", icon: CreditCard, route: ROUTES.ADMIN_SUBSCRIPTION, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.billingHistory", icon: FileText, route: ROUTES.ADMIN_BILLING_HISTORY, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.tutorQuotas", icon: Gauge, route: ROUTES.ADMIN_TUTOR_QUOTAS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.quotaDashboard", icon: Gauge, route: ROUTES.ADMIN_QUOTA_DASHBOARD, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.analytics", icon: LineChart, route: ROUTES.ADMIN_ANALYTICS, roles: [USER_ROLES.SCHOOL_ADMIN], exactActiveRoutes: [ROUTES.ADMIN_ANALYTICS] },
      {
        labelKey: "sidebar.admin.studentUsage",
        icon: BarChart2,
        route: ROUTES.ADMIN_ANALYTICS_STUDENTS,
        roles: [USER_ROLES.SCHOOL_ADMIN],
        activeRoutes: [ROUTES.ADMIN_ANALYTICS_STUDENTS],
      },
      { labelKey: "sidebar.admin.activityLogs", icon: ClipboardList, route: ROUTES.ADMIN_ACTIVITY_LOGS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.emailConfigs", icon: Settings, route: ROUTES.ADMIN_COMMS_EMAIL_CONFIGS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.emailTemplates", icon: FileText, route: ROUTES.ADMIN_COMMS_EMAIL_TEMPLATES, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.weeklyReports", icon: BarChart2, route: ROUTES.ADMIN_WEEKLY_REPORTS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.tutorTheme", icon: Sparkles, route: ROUTES.ADMIN_TUTOR_THEME, roles: [USER_ROLES.SCHOOL_ADMIN] },
      buildSupportNavItem(ROUTES.ADMIN_SUPPORT, [USER_ROLES.SCHOOL_ADMIN], 'customer-school')
    );
  }

  // TEACHER
  if (user.roles.includes(USER_ROLES.TEACHER)) {
    navItems.push(
      { labelKey: "sidebar.teacher.dashboard", icon: LayoutDashboard, route: ROUTES.TEACHER_DASHBOARD, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.mySections", icon: BookOpen, route: ROUTES.TEACHER_MY_SECTIONS, roles: [USER_ROLES.TEACHER] },
      {
        labelKey: "sidebar.teacher.assignments",
        icon: FileText,
        route: ROUTES.TEACHER_ASSIGNMENTS,
        roles: [USER_ROLES.TEACHER],
        activeRoutes: [ROUTES.TEACHER_ASSIGNMENTS, ROUTES.TEACHER_COMMUNICATION_ASSIGNMENTS],
      },
      {
        labelKey: "sidebar.teacher.tutorAnalytics",
        icon: BarChart2,
        route: ROUTES.TEACHER_TUTOR_ANALYTICS,
        roles: [USER_ROLES.TEACHER],
        activeRoutes: [ROUTES.TEACHER_TUTOR_ANALYTICS, ROUTES.TEACHER_ANALYTICS],
      },
      {
        labelKey: "sidebar.teacher.classProgress",
        icon: Users,
        route: ROUTES.TEACHER_CLASS_PROGRESS,
        roles: [USER_ROLES.TEACHER],
        activeRoutes: [
          ROUTES.TEACHER_CLASS_PROGRESS,
          ROUTES.TEACHER_STUDENT_COMMUNICATION_PROGRESS,
        ],
      },
      {
        labelKey: "sidebar.teacher.content",
        icon: BookOpen,
        route: ROUTES.TEACHER_CONTENT_LESSONS,
        roles: [USER_ROLES.TEACHER],
        activeRoutes: [ROUTES.TEACHER_CONTENT],
      },
      {
        labelKey: "sidebar.teacher.canDo",
        icon: Tag,
        route: ROUTES.TEACHER_CAN_DO,
        roles: [USER_ROLES.TEACHER],
        // Do not mark Can-Do active for Class Progress / legacy student-progress redirects.
        activeRoutes: [
          ROUTES.TEACHER_CAN_DO,
          ROUTES.TEACHER_COMMUNICATION_ANALYTICS,
          ROUTES.TEACHER_COMMUNICATION_ALERTS,
        ],
      },
      { labelKey: "sidebar.teacher.scenarioAuthoring", icon: MessageSquare, route: ROUTES.TEACHER_SCENARIO_AUTHORING, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.profile", icon: UserCheck, route: ROUTES.TEACHER_PROFILE, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.settings", icon: Settings, route: ROUTES.TEACHER_SETTINGS, roles: [USER_ROLES.TEACHER] },
      buildSupportNavItem(ROUTES.TEACHER_SUPPORT, [USER_ROLES.TEACHER], 'customer')
    );
  }

  // STUDENT — curriculum-aware POC nav (Malay / Tamil / neutral loading-safe)
  if (user.roles.includes(USER_ROLES.STUDENT)) {
    for (const def of buildStudentPocNavItems({
      track: learnerPocTrack,
      rosterGrade,
    })) {
      navItems.push({
        labelKey: def.labelKey,
        icon: POC_NAV_ICONS[def.iconKey],
        route: def.route,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: def.schoolFeatureKey,
        activeRoutes: def.activeRoutes,
      });
    }
  }

  if (user.roles.includes(USER_ROLES.SUPPORT_AGENT) && !user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) {
    navItems.push(buildSupportNavItem(ROUTES.ADMIN_SUPPORT, [USER_ROLES.SUPPORT_AGENT], 'operator'));
  }

  // PARENT
  if (user.roles.includes(USER_ROLES.PARENT)) {
    navItems.push(
      { labelKey: "sidebar.parent.dashboard", icon: LayoutDashboard, route: ROUTES.PARENT_DASHBOARD, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.children", icon: Users, route: ROUTES.PARENT_CHILDREN, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.progress", icon: BarChart2, route: ROUTES.PARENT_COMMUNICATION_PROGRESS, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.linkChild", icon: UserCheck, route: ROUTES.PARENT_LINK_CHILD, roles: [USER_ROLES.PARENT] },
      buildSupportNavItem(ROUTES.PARENT_SUPPORT, [USER_ROLES.PARENT], 'customer')
    );
  }

  const filteredNavItems = navItems.filter((item) => {
    if (!item.roles.some((role) => user.roles.includes(role as any))) return false;
    if (item.schoolFeatureKeysAny?.length) {
      if (featureAccessLoading) return false;
      if (!item.schoolFeatureKeysAny.some((k) => isSchoolFeatureEnabled(k))) return false;
    } else if (item.schoolFeatureKey) {
      if (featureAccessLoading) return false;
      if (!isSchoolFeatureEnabled(item.schoolFeatureKey)) return false;
    }
    return true;
  });

  const activePill = (
    <span
      className="absolute -left-px top-1/2 h-8 w-1.5 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-app-primary via-app-primary/90 to-app-primary/75 shadow-sm"
      aria-hidden
    />
  );

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-gradient-to-b from-app-primary/10 via-app-primary/5 to-app-primary/10 dark:from-gray-950 dark:via-app-primary/10 dark:to-gray-950"
      aria-hidden={!isOpen}
    >
      <div className="lg:hidden flex items-center justify-end p-3 shrink-0" />

      <nav
        className="flex-1 min-h-0 overflow-y-auto px-3 pt-3 pb-2 space-y-2 sidebarNavScroll"
        aria-busy={!sidebarReady}
      >
        <style>{`
          .sidebarNavScroll::-webkit-scrollbar { width: 5px; }
          .sidebarNavScroll::-webkit-scrollbar-thumb { background: rgb(var(--app-primary) / 0.25); border-radius: 999px; }
        `}</style>
        {!sidebarReady ? (
          <SidebarNavListSkeleton />
        ) : (
          <>
          {filteredNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = isNavItemActive(location, item);
          const showTutorAccent =
            isStudent && isActive && item.route === ROUTES.STUDENT_TAMIL_TUTOR;

          if (item.children) {
            return (
              <div key={item.route}>
                <button
                  type="button"
                  onClick={() => setPeopleOpen((v) => !v)}
                  aria-expanded={peopleOpen}
                  className={clsx(
                    "group w-full text-left",
                    NAV_CARD,
                    isActive ? NAV_ACTIVE : NAV_INACTIVE
                  )}
                >
                  {isActive ? activePill : null}
                  <div className={clsx(NAV_ICON_WRAP, isActive && NAV_ICON_ACTIVE)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className={clsx(
                      "tracking-wide leading-tight flex-1",
                      isActive ? NAV_LABEL_ACTIVE : NAV_LABEL_INACTIVE
                    )}
                  >
                    {navLabel(item.labelKey)}
                  </span>
                  {peopleOpen ? (
                    <ChevronDown className="w-4 h-4 shrink-0 opacity-70 text-app-primary dark:text-app-primary/90" />
                  ) : (
                    <ChevronRight className="w-4 h-4 shrink-0 opacity-70 text-app-primary dark:text-app-primary/90" />
                  )}
                </button>
                {peopleOpen && (
                  <div className="ml-4 pl-3 mt-1.5 mb-1 space-y-1.5 border-l-2 border-app-primary/25 dark:border-app-primary/35">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive =
                        location.pathname === child.route ||
                        location.pathname.startsWith(child.route + "/");
                      return (
                        <Link
                          key={child.route}
                          to={child.route}
                          onClick={() => {
                            if (
                              typeof window !== "undefined" &&
                              window.matchMedia("(max-width: 767px)").matches
                            ) {
                              onClose();
                            }
                          }}
                          className={clsx(
                            NAV_CARD,
                            "relative flex items-center gap-2 px-3 py-2",
                            childActive ? NAV_ACTIVE : NAV_INACTIVE
                          )}
                        >
                          {childActive ? activePill : null}
                          <ChildIcon className="h-[18px] w-[18px] shrink-0 text-app-primary dark:text-app-primary/90" />
                          <span
                            className={clsx(
                              "truncate text-sm",
                              childActive ? NAV_LABEL_ACTIVE : NAV_LABEL_INACTIVE
                            )}
                          >
                            {navLabel(child.labelKey)}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.route}
              to={item.route}
              aria-current={isActive ? "page" : undefined}
              onClick={() => {
                if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
                  onClose();
                }
              }}
              className={clsx(NAV_CARD, isActive ? NAV_ACTIVE : NAV_INACTIVE)}
            >
              {isActive ? activePill : null}
              <div className={clsx(NAV_ICON_WRAP, isActive && NAV_ICON_ACTIVE)}>
                <Icon className="w-5 h-5" />
              </div>
              <span
                className={clsx(
                  "tracking-wide leading-tight flex-1",
                  isActive ? NAV_LABEL_ACTIVE : NAV_LABEL_INACTIVE
                )}
              >
                {navLabel(item.labelKey)}
              </span>
              {showTutorAccent && (
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-app-primary/15 dark:bg-app-primary/25 text-app-primary"
                  aria-hidden
                >
                  <Music2 size={16} />
                </span>
              )}
            </Link>
          );
        })}
        {isStudent && curriculumError ? (
          <div className="rounded-2xl border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100">
            <p className="font-medium">Could not load your learning track.</p>
            <button
              type="button"
              className="mt-1 font-semibold underline"
              onClick={() => reloadCurriculum()}
            >
              Retry
            </button>
          </div>
        ) : null}
          </>
        )}
      </nav>

      {isStudent && !isPocIbooksOnlyStudent && (
        <div className="shrink-0 px-3 pt-3 pb-2 mt-1 border-t-2 border-app-primary/20 dark:border-app-primary/30">
          <SidebarMyProgress />
        </div>
      )}

      <div className="relative shrink-0 px-3 pb-4 pt-1">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12" aria-hidden>
          <svg viewBox="0 0 400 60" className="w-full h-full" preserveAspectRatio="none">
            <path
              d="M0,30 Q100,55 200,35 T400,28 L400,60 L0,60 Z"
              className="fill-app-primary/25 dark:fill-app-primary/30"
            />
          </svg>
        </div>
        {isStudent && !isPocIbooksOnlyStudent ? (
          <div className="relative rounded-2xl bg-white/80 dark:bg-gray-900/80 border border-app-primary/15 dark:border-app-primary/30 shadow-sm overflow-hidden">
            <Link
              to={ROUTES.STUDENT_PROFILE}
              aria-current={
                location.pathname === ROUTES.STUDENT_PROFILE ||
                location.pathname.startsWith(`${ROUTES.STUDENT_PROFILE}/`)
                  ? "page"
                  : undefined
              }
              onClick={() => {
                if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
                  onClose();
                }
              }}
              className={clsx(
                "flex w-full items-center justify-center gap-1.5 py-2.5 text-[11px] transition",
                location.pathname === ROUTES.STUDENT_PROFILE ||
                  location.pathname.startsWith(`${ROUTES.STUDENT_PROFILE}/`)
                  ? "bg-app-primary/15 dark:bg-app-primary/20"
                  : "hover:bg-app-primary/10 dark:hover:bg-app-primary/15"
              )}
            >
              <Settings
                size={14}
                className="shrink-0 text-app-primary dark:text-app-primary/90"
                aria-hidden
              />
              <span
                className={clsx(
                  "font-semibold",
                  location.pathname === ROUTES.STUDENT_PROFILE ||
                    location.pathname.startsWith(`${ROUTES.STUDENT_PROFILE}/`)
                    ? NAV_LABEL_ACTIVE
                    : NAV_LABEL_INACTIVE
                )}
              >
                {t("common.settings", { defaultValue: "Settings" })}
              </span>
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};